import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { EmptyState } from "../components/shared/EmptyState";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FolderKanban, Plus } from "lucide-react";
import { VoiceButton } from "../components/shared/VoiceButton";
import { capture, EVENTS } from "../analytics/posthog";

const STATUS = {
  DRAFT: { l: "Borrador", c: "text-slate-400 border-slate-500/30 bg-slate-500/10" },
  ACTIVE: { l: "Activo", c: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10" },
  READY_FOR_REVIEW: { l: "Para revisar", c: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
  BLOCKED_CREDITS: { l: "Sin créditos", c: "text-red-400 border-red-500/30 bg-red-500/10" },
  COMPLETED: { l: "Completado", c: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
  PAUSED: { l: "Pausado", c: "text-slate-400 border-slate-500/30 bg-slate-500/10" },
  ARCHIVED: { l: "Archivado", c: "text-slate-500 border-slate-500/20 bg-slate-500/5" },
};

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [obj, setObj] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/projects").then(({ data }) => setProjects(data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!obj.trim()) return;
    setCreating(true);
    try { const { data } = await api.post("/projects", { objective: obj.trim() }); capture(EVENTS.projectCreated, { source: "projects" }); navigate(`/app/projects/${data.id}`); }
    catch (e) { toast.error(apiErr(e)); setCreating(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Proyectos</h1>
        <p className="mt-1 text-slate-400">La unidad de trabajo de Architect para reducir dependencias.</p>
      </div>

      <div className="card-arch p-5">
        <div className="flex flex-wrap sm:flex-nowrap items-end gap-2">
          <input data-testid="projects-new-input" value={obj} onChange={(e) => setObj(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Nuevo objetivo. Ej. Reducir dependencia de María"
            className="min-w-0 basis-full sm:basis-auto flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40" />
          <VoiceButton testId="projects-voice-button" onTranscript={(t) => setObj((p) => (p ? p + " " : "") + t)} />
          <Button data-testid="projects-create-button" onClick={create} disabled={creating || !obj.trim()}
            className="h-11 flex-1 sm:flex-none rounded-xl bg-amber-500 text-black hover:bg-amber-400 font-semibold px-4"><Plus className="mr-1 h-4 w-4" /> Crear</Button>
        </div>
      </div>

      {loading ? <div className="mono-label text-slate-500">CARGANDO…</div> :
        projects.length === 0 ? <EmptyState icon={FolderKanban} title="Sin proyectos todavía" description="Crea tu primer proyecto arriba o desde tu Independence Plan." /> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => {
            const st = STATUS[p.status] || STATUS.ACTIVE;
            return (
              <button key={p.id} data-testid={`project-card-${p.id}`} onClick={() => navigate(`/app/projects/${p.id}`)}
                className="card-arch p-5 text-left">
                <div className="flex items-center justify-between">
                  <span className={`rounded-full border px-2.5 py-0.5 text-[0.65rem] font-mono ${st.c}`}>{st.l}</span>
                  <span className="mono-label text-slate-500">{p.progress}%</span>
                </div>
                <h3 className="mt-3 font-head text-base font-semibold text-white line-clamp-2">{p.objective}</h3>
                <div className="mt-4 h-1 rounded-full bg-white/5"><div className="h-full rounded-full bg-amber-500" style={{ width: `${p.progress}%` }} /></div>
                <p className="mt-3 text-xs text-slate-500">{(p.system_ids || []).length} sistema(s) generado(s)</p>
              </button>
            );
          })}
        </div>}
    </div>
  );
}
